import type { ReactNode } from 'react'

import { cx } from '../lib/cx'
import { ICONO_SEVERIDAD, Icon } from './Icon'
import { SEVERIDAD_TEXTO, SEVERIDAD_TONO, type Severidad } from './tokens'

/**
 * Etiqueta de estado con fondo tintado. El texto es obligatorio (`children`): el
 * fondo de color es refuerzo y nunca la única señal.
 *
 * Sobre los fondos `*-soft` el texto va en `--text` o en el nivel de texto del
 * color, nunca en el color de marca crudo ni en blanco.
 */
export interface StatusPillProps {
  /** El estado en palabras. Obligatorio. */
  children: ReactNode
  severidad?: Severidad
  /** Muestra el icono de la severidad a la izquierda. */
  icono?: boolean
  size?: 'sm' | 'md'
  /** MAYÚSCULAS con tracking: el tratamiento de las pills de severidad. */
  caps?: boolean
  /** Punto de datos crudo para debug y para los tests. */
  testId?: string
  className?: string
}

export function StatusPill({
  children,
  severidad = 'unknown',
  icono = true,
  size = 'md',
  caps,
  testId = 'status-pill',
  className,
}: StatusPillProps) {
  return (
    <span
      data-testid={testId}
      data-severidad={severidad}
      className={cx(
        'inline-flex max-w-full items-center gap-1.5 rounded border leading-4',
        SEVERIDAD_TONO[severidad],
        size === 'sm' ? 'px-1.5 py-0.5 text-micro' : 'px-2 py-0.5 text-meta',
        caps === true
          ? 'font-bold uppercase tracking-[0.06em]'
          : 'font-semibold',
        // Sobre un fondo tintado el texto va en el nivel legible del color.
        SEVERIDAD_TEXTO[severidad],
        className,
      )}
    >
      {icono ? (
        <Icon
          name={ICONO_SEVERIDAD[severidad]}
          size={size === 'sm' ? 10 : 12}
          className="mt-px"
        />
      ) : null}
      <span className="truncate">{children}</span>
    </span>
  )
}
